import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, Clock, Save, Plus, Trash2, CheckCircle2 } from "lucide-react";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const DEFAULT_SCHEDULE = DAYS.map(day => ({
  day,
  available: !["Sunday"].includes(day),
  start: "08:00",
  end: "18:00",
}));

export default function ManageAvailabilityPage() {
  const navigate = useNavigate();
  const [schedule, setSchedule] = useState(DEFAULT_SCHEDULE);
  const [status, setStatus] = useState("available");
  const [saved, setSaved] = useState(false);

  const toggleDay = (i) => setSchedule(s => s.map((d, idx) => idx === i ? { ...d, available: !d.available } : d));
  const updateTime = (i, field, val) => setSchedule(s => s.map((d, idx) => idx === i ? { ...d, [field]: val } : d));

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const statusOptions = [
    { value: "available", label: "Available", color: "#16a34a", bg: "#f0fdf4" },
    { value: "busy", label: "Busy", color: "#b45309", bg: "#fffbeb" },
    { value: "unavailable", label: "Unavailable", color: "#6b7280", bg: "#f9fafb" },
  ];

  return (
    <div style={{ background: "#f8fafc", minHeight: "100vh", fontFamily: "'DM Sans', 'Helvetica Neue', Arial, sans-serif" }}>
      <header style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "0 16px", height: 60, display: "flex", alignItems: "center", gap: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
        <button onClick={() => navigate(-1)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 6, color: "#475569", fontWeight: 600, fontSize: 14 }}>
          <ArrowLeft size={18} strokeWidth={1.75} /> Back
        </button>
        <span style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>Manage Availability</span>
      </header>

      <main style={{ maxWidth: 600, margin: "0 auto", padding: "24px 16px 80px" }}>
        <section style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>Current Status</h2>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {statusOptions.map(opt => (
              <button key={opt.value} onClick={() => setStatus(opt.value)} style={{
                padding: "8px 16px", borderRadius: 20, border: `1.5px solid ${status === opt.value ? opt.color : "#e2e8f0"}`,
                background: status === opt.value ? opt.bg : "#fff",
                color: status === opt.value ? opt.color : "#64748b",
                fontWeight: 600, fontSize: 13, cursor: "pointer",
              }}>
                {opt.label}
              </button>
            ))}
          </div>
        </section>

        <section style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: "#0f172a", marginBottom: 12 }}>Weekly Schedule</h2>
          <div style={{ background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            {schedule.map((row, i) => (
              <div key={row.day} style={{
                display: "flex", alignItems: "center", gap: 12, padding: "14px 16px",
                borderBottom: i < schedule.length - 1 ? "1px solid #f1f5f9" : "none",
                opacity: row.available ? 1 : 0.45,
              }}>
                <button
                  onClick={() => toggleDay(i)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: 0, flexShrink: 0 }}
                >
                  <div style={{
                    width: 22, height: 22, borderRadius: 6, border: `1.5px solid ${row.available ? "#2d6a9f" : "#cbd5e1"}`,
                    background: row.available ? "#2d6a9f" : "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    {row.available && <CheckCircle2 size={13} color="#fff" strokeWidth={2.5} />}
                  </div>
                </button>
                <span style={{ width: 90, fontSize: 13, fontWeight: 600, color: "#0f172a", flexShrink: 0 }}>{row.day}</span>
                {row.available ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input type="time" value={row.start} onChange={e => updateTime(i, "start", e.target.value)}
                      style={{ padding: "6px 8px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, color: "#0f172a" }} />
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>to</span>
                    <input type="time" value={row.end} onChange={e => updateTime(i, "end", e.target.value)}
                      style={{ padding: "6px 8px", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 13, color: "#0f172a" }} />
                  </div>
                ) : (
                  <span style={{ fontSize: 13, color: "#94a3b8" }}>Not available</span>
                )}
              </div>
            ))}
          </div>
        </section>

        <button
          onClick={handleSave}
          style={{
            width: "100%", padding: 16, background: saved ? "#16a34a" : "#1e3a5f",
            border: "none", borderRadius: 14, color: "#fff", fontSize: 15, fontWeight: 700,
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
            transition: "background 0.2s",
          }}
        >
          {saved ? <><CheckCircle2 size={18} /> Saved!</> : <><Save size={18} /> Save Availability</>}
        </button>
      </main>
    </div>
  );
}